import { type ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import { MagnifyingGlassIcon, XSmallCircleColorIcon } from '@/icons';
import { ClassroomSortDropdown, type ClassroomSortMode } from '@/pages/classrooms/shared/components/ClassroomSortDropdown';
import { TextField, TextFieldSlot } from '@/ui_v3';
import { cn } from '@/utils';
export type ClassroomListSortMode = ClassroomSortMode;
export function ClassroomSearchField({
  className,
  searchText,
  autoFocus,
  onSearchTextChange,
  onSearchSubmit
}: {
  className?: string;
  searchText: string;
  autoFocus?: boolean;
  onSearchTextChange: (value: string) => void;
  onSearchSubmit: (value: string) => void;
}) {
  const {
    t
  } = useTranslation(['classroom']);
  return <form className={cn('min-w-0 max-w-[400px] flex-1', className)} onSubmit={e => {
    e.preventDefault();
    onSearchSubmit(searchText);
  }} data-sentry-component="ClassroomSearchField" data-sentry-source-file="ClassroomListSearchToolbar.tsx">
      <TextField value={searchText} onChange={e => onSearchTextChange(e.target.value)} placeholder={t('classroom:classroomListSearchToolbar.searchPlaceholder')}
    // 학생 목록 검색창(ClassroomContentHeader)과 같은 크기로 맞춘다.
    // zus TextField: neutral 48px / strong 58px. 모바일은 아래 오버라이드가 40px.
    intent="neutral" autoFocus={autoFocus}
    // required 를 두면 빈 검색어 제출을 브라우저 validation 이 막아
    // 검색어를 지우고 Enter 로 전체 목록으로 되돌릴 수 없다.
    className="w-full max-mobile-lg:!h-[40px] max-mobile-lg:!min-h-[40px] max-mobile-lg:!py-xs max-mobile-lg:text-body-lg-medium" rightSlot={<div className="flex items-center gap-2xs">
            {searchText && <button type="button" className="flex size-[16px] shrink-0 items-center justify-center" aria-label={t('classroom:classroomListSearchToolbar.clearSearchAriaLabel')} onClick={() => {
        onSearchTextChange('');
        onSearchSubmit('');
      }}>
                <XSmallCircleColorIcon size="xs" />
              </button>}
            <button type="submit" className="flex shrink-0 items-center justify-center" aria-label={t('classroom:classroomListSearchToolbar.searchAriaLabel')}>
              <TextFieldSlot.Icon icon={MagnifyingGlassIcon} size="md" />
            </button>
          </div>} data-sentry-element="TextField" data-sentry-source-file="ClassroomListSearchToolbar.tsx" />
    </form>;
}
export function ClassroomListSearchToolbar({
  filteredItemCount,
  isFetchingSortOrder,
  searchText,
  sortMode,
  leading,
  compact = false,
  onChangeSortMode,
  onSearchTextChange,
  onSearchSubmit
}: {
  filteredItemCount: number;
  isFetchingSortOrder: boolean;
  searchText: string;
  sortMode: ClassroomListSortMode;
  leading?: ReactNode;
  compact?: boolean;
  onChangeSortMode: (mode: ClassroomListSortMode) => void;
  onSearchTextChange: (value: string) => void;
  onSearchSubmit: (value: string) => void;
}) {
  const {
    t
  } = useTranslation(['classroom']);
  return <div className="flex flex-col gap-sm" data-sentry-component="ClassroomListSearchToolbar" data-sentry-source-file="ClassroomListSearchToolbar.tsx">
      <div className={cn('flex items-center justify-between gap-xs pb-xs mobile-lg:gap-sm', leading && 'max-mobile-lg:flex-col max-mobile-lg:items-start', !leading && 'max-mobile-lg:justify-end')}>
        {leading ? <div className="min-w-0 shrink-0">{leading}</div> : null}
        <div className={cn('flex min-w-0 flex-1 items-center gap-sm mobile-lg:gap-md', leading ? 'justify-end max-mobile-lg:w-full max-mobile-lg:justify-between max-mobile-lg:pt-xs' : 'justify-between')}>
          <ClassroomSearchField searchText={searchText} onSearchTextChange={onSearchTextChange} onSearchSubmit={onSearchSubmit} data-sentry-element="ClassroomSearchField" data-sentry-source-file="ClassroomListSearchToolbar.tsx" />
          <ClassroomSortDropdown countLabel={t('classroom:classroomList.countLabel', {
          count: filteredItemCount
        })} sortMode={sortMode} onChangeSortMode={onChangeSortMode} disabled={isFetchingSortOrder} compact={compact} data-sentry-element="ClassroomSortDropdown" data-sentry-source-file="ClassroomListSearchToolbar.tsx" />
        </div>
      </div>
    </div>;
}